import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import GoldPurchaseBanner from '../components/GoldPurchaseBanner';
import InstagramSection from '../components/InstagramSection';
import { companyInfo, openingHours } from '../data/siteData';

function ParallaxBanner({ src, alt }) {
  const [offsetY, setOffsetY] = useState(0);
  const bannerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!bannerRef.current) return;
      const rect = bannerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (rect.top <= windowHeight && rect.bottom >= 0) {
        const center = rect.top + rect.height / 2;
        const screenCenter = windowHeight / 2;
        const diff = screenCenter - center;
        setOffsetY(diff * 0.22);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={bannerRef} className="w-full overflow-hidden border-y border-[#E2DDD3] relative h-[380px] sm:h-[480px]">
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-[140%] -top-[20%] object-cover transition-transform duration-75 ease-out"
        style={{ transform: `translate3d(0, ${offsetY}px, 0)` }}
      />
      <div className="absolute inset-0 bg-[#0B132B]/60 backdrop-blur-[1px]"></div>
    </section>
  );
}

export default function HomePage() {
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);

  const heroImages = [
    "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-137-639989608-2046706026108778-3908745731602171548-n.jpg",
    "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-767222368-2201547903957922-6641466863669271029-n-1.jpg",
    "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-749056519-2178237346288978-7575095009079936217-n.jpg",
    "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-654868285-2071687183610662-3515381818335373868-n.jpg"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const coreAreas = [
    {
      stat: "35+",
      label: "ÅRS ERFARING",
      title: "VI KØBER GULD & SØLV",
      subtitle: "Højeste dagspris & uforpligtende vurdering",
      description: "Aflever dit gamle guld, arvestykker, mønter eller sølv og få en hurtig, professionel vurdering i trygge rammer.",
      link: "/vi-koeber-guld-og-soelv",
      badge: "Mest populære",
      icon: "fa-coins"
    },
    {
      stat: "100%",
      label: "LOKALT VÆRKSTED",
      title: "GULDSMEDEVÆRKSTED",
      subtitle: "Traditionelt håndværk i Brønshøj",
      description: "Lodning af kæder, ringstørrelse-ændring, isætning af diamanter og rens af værdifulde smykker.",
      link: "/guldsmedevaerksted",
      icon: "fa-gem"
    },
    {
      stat: "UNIKA",
      label: "SKRÆDDERSYET",
      title: "SMYKKER EFTER EGET DESIGN",
      subtitle: "Unika smykker skabt til dig",
      description: "Drømmer du om et specielt smykke? Vi designer og fremstiller unika smykker efter dine ønsker.",
      link: "/smykker-eget-design",
      icon: "fa-wand-magic-sparkles"
    },
    {
      stat: "FAG",
      label: "URMAGERSERVICE",
      title: "UREREPARATION",
      subtitle: "Faguddannet service af ure",
      description: "Batteriskift, trykprøvning, udskiftning af glas/rem samt fuld renovering af mekaniske urværker.",
      link: "/urereparation",
      icon: "fa-clock"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Forside - Brønshøj Ure & Guld</title>
        <meta
          name="description"
          content="Guldsmed i Brønshøj Hos Brønshøj Ure & Guld har vi et stort udvalg af Smyker og ure. Smykker og ure er med til at afspejle din personlighed overfor omverdenen."
        />
        <meta property="og:title" content="Forside - Brønshøj Ure & Guld" />
        <meta
          property="og:description"
          content="Guldsmed i Brønshøj Hos Brønshøj Ure & Guld har vi et stort udvalg af Smyker og ure. Smykker og ure er med til at afspejle din personlighed overfor omverdenen."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/807b5052-bea6-494e-8947-b0c29529a13c/elegant-black-and-white-logo-design-2.png"
        />
      </Helmet>

      {/* SECTION 1: HERO BANNER (Full-Bleed Luxury Dark Block Behind Fixed Header) */}
      <section className="w-full min-h-screen relative flex flex-col justify-center items-center text-center bg-[#0B132B] text-white px-6 sm:px-12 pt-32 sm:pt-40 lg:pt-44 pb-16 lg:pb-24 border-b border-[#E2DDD3]/20 overflow-hidden">
        {heroImages.map((imgUrl, index) => (
          <div
            key={imgUrl}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out ${
              index === currentHeroSlide ? 'opacity-80 scale-100' : 'opacity-0 scale-105 pointer-events-none'
            }`}
            style={{ backgroundImage: `url(${imgUrl})` }}
          ></div>
        ))}
        {/* Transparent luxury overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B132B]/70 via-[#0B132B]/20 to-[#0B132B]/85"></div>

        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center space-y-6 py-12">
          
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.3em] text-[#B5A897] font-semibold px-2 text-center max-w-full block">
            LOKAL GULDSMED & URMAGER I BRØNSHØJ
          </span>

          <h1 className="font-serif text-3xl sm:text-6xl lg:text-7xl uppercase tracking-[0.15em] sm:tracking-[0.25em] text-white font-normal leading-tight break-words max-w-full">
            BRØNSHØJ <span className="inline-block whitespace-nowrap">URE & GULD</span>
          </h1>

          <p className="text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.2em] uppercase text-stone-200 max-w-xl font-sans leading-relaxed drop-shadow-sm px-2">
            Eksklusivt håndværk, personlig rådgivning & guldkøb med over 35 års erfaring
          </p>

          <div className="pt-6 grid grid-cols-3 gap-2 sm:gap-4 w-full max-w-2xl mx-auto">
            <Link
              to="/vi-koeber-guld-og-soelv"
              className="px-2 sm:px-4 py-3 sm:py-3.5 border border-white/60 text-white bg-[#0B132B]/40 backdrop-blur-xs hover:bg-white hover:text-[#0B132B] transition-all text-[9px] sm:text-xs tracking-[0.1em] sm:tracking-[0.2em] uppercase font-medium rounded-none text-center flex items-center justify-center"
            >
              FÅ VURDERET DIT GULD
            </Link>

            <Link
              to="/brands"
              className="px-2 sm:px-4 py-3 sm:py-3.5 border border-white/60 text-white bg-[#0B132B]/40 backdrop-blur-xs hover:bg-white hover:text-[#0B132B] transition-all text-[9px] sm:text-xs tracking-[0.1em] sm:tracking-[0.2em] uppercase font-medium rounded-none text-center flex items-center justify-center"
            >
              VORES MÆRKER
            </Link>

            <Link
              to="/guldsmedevaerksted"
              className="px-2 sm:px-4 py-3 sm:py-3.5 border border-[#B5A897] text-[#B5A897] bg-[#0B132B]/40 backdrop-blur-xs hover:bg-[#B5A897] hover:text-[#0B132B] transition-all text-[9px] sm:text-xs tracking-[0.1em] sm:tracking-[0.2em] uppercase font-medium rounded-none text-center flex items-center justify-center"
            >
              VORES VÆRKSTED
            </Link>
          </div>

        </div>
      </section>

      {/* SECTION 2: BRAND NARRATIVE (Editorial Side-by-Side Layout) */}
      <section className="relative bg-[#F8F7F5] py-20 lg:py-28 px-4 sm:px-8 lg:px-12 flex items-center justify-center overflow-hidden border-b border-[#D8CFC7]/40">
        <div className="relative max-w-7xl xl:max-w-[1380px] w-full grid grid-cols-1 xl:grid-cols-12 items-center">

          {/* Left Floating Image (Asymmetrical Overlapping Grid - Left Offset) */}
          <div className="hidden xl:block xl:col-span-4 z-20 relative xl:-top-[80px] xl:left-[20px]">
            <div className="h-[450px] xl:h-[550px] max-h-[450px] xl:max-h-[550px] w-full max-w-[400px] overflow-hidden shadow-2xl border border-[#D8CFC7]/60 mx-auto">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-691259830-2111687916277255-229202910115914980-n.jpg"
                alt="Guldsmedeværksted i Brønshøj"
                className="w-full h-full object-cover rounded-none max-w-[400px]"
              />
            </div>
          </div>

          {/* Center Content Block (Warm Surface Card) */}
          <div className="col-span-1 xl:col-span-4 bg-[#F1EBE7] z-10 py-16 px-8 sm:px-12 xl:px-14 text-center border border-[#D8CFC7] shadow-sm my-2 xl:-mx-[30px] max-w-2xl xl:max-w-none mx-auto w-full">
            
            {/* Top Architectural Line Divider */}
            <div className="w-[1px] h-16 bg-[#D8CFC7] mx-auto mb-8"></div>

            {/* Main Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl uppercase tracking-[0.22em] text-[#BEADA3] font-light leading-snug mb-4">
              GULDSMED<br />I BRØNSHØJ
            </h2>

            {/* Meta Date / Subhead */}
            <p className="font-serif italic text-xs sm:text-sm text-[#6E6863] mb-8 tracking-widest">
              OVER 35 ÅRS ERFARING
            </p>

            {/* Paragraph Content */}
            <div className="font-sans text-xs sm:text-sm text-[#2B2927] leading-relaxed max-w-md mx-auto space-y-4 mb-10">
              <p>
                Hos Brønshøj Ure & Guld har vi et stort udvalg af smykker og ure. Smykker og ure er med til at afspejle din personlighed overfor omverdenen.
              </p>
              <p>
                Nogle er til enkelhed, mens andre ikke kan få diamanter nok. Uanset hvilken type du er, så kom ind og bliv inspireret til at købe dit næste smykke eller ur hos os.
              </p>
            </div>

            {/* Call to Action Link */}
            <div className="inline-block">
              <Link
                to="/om-os"
                className="font-sans text-[11px] uppercase tracking-[0.25em] font-medium text-[#2B2927] border-b border-[#2B2927] pb-1 hover:text-[#BEADA3] hover:border-[#BEADA3] transition-colors"
              >
                LÆS MERE OM OS
              </Link>
            </div>

          </div>

          {/* Right Floating Image (Asymmetrical Overlapping Grid - Right Offset) */}
          <div className="hidden xl:block xl:col-span-4 z-20 relative xl:-ml-20 xl:mt-[160px] translate-x-[20px]">
            <div className="h-[450px] xl:h-[550px] max-h-[450px] xl:max-h-[550px] w-full max-w-[400px] overflow-hidden shadow-2xl border border-[#D8CFC7]/60 mx-auto">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-749056519-2178237346288978-7575095009079936217-n.jpg"
                alt="Eksklusive smykker og detaljer"
                className="w-full h-full object-cover rounded-none max-w-[400px]"
              />
            </div>
          </div>

          {/* Mobile/Tablet/1024px Image Grid (Visible below central card on screens up to 1280px) */}
          <div className="grid grid-cols-2 gap-4 sm:gap-6 w-full max-w-2xl mx-auto mt-8 sm:mt-12 xl:hidden col-span-1">
            <div className="aspect-[3/4] overflow-hidden border border-[#D8CFC7] shadow-md">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-691259830-2111687916277255-229202910115914980-n.jpg"
                alt="Guldsmedeværksted"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[3/4] overflow-hidden border border-[#D8CFC7] shadow-md">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-749056519-2178237346288978-7575095009079936217-n.jpg"
                alt="Eksklusive smykker og detaljer"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 3: FEATURED ACCOMMODATION / PROMINENT COMMERCIAL FOCUS (2-Column Split) */}
      <GoldPurchaseBanner />

      {/* SECTION 5: CORE AREAS SHOWCASE (Redesigned per "Counter" Reference layout) */}
      <section className="w-full bg-[#F9F8F6] pt-16 pb-24">
        
        {/* Top Centered Introductory Text Block */}
        <div className="text-center max-w-2xl mx-auto px-4 space-y-3 mb-12">
          <div className="w-[1px] h-8 bg-[#B5A897] mx-auto mb-3"></div>
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#B5A897] font-semibold block">
            SPECIALER & YDELSER
          </span>
          <h2 className="font-serif text-3xl sm:text-5xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
            VORES KERNEOMRÅDER
          </h2>
          <p className="text-xs text-stone-600 leading-relaxed font-sans max-w-md mx-auto tracking-widest uppercase">
            Faguddannet rådgivning, guldkøb og traditionelt guldsmedehåndværk i Brønshøj
          </p>
        </div>

        {/* Full-Bleed Background Image Banner */}
        <div className="relative w-full h-[320px] sm:h-[420px] overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-767222368-2201547903957922-6641466863669271029-n-1.jpg"
            alt="Kerneområder og specialer"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0B132B]/60 backdrop-blur-[1px]"></div>
        </div>

        {/* Overlapping Grid of 4 Cards Positioned Across Lower Boundary */}
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 -mt-20 sm:-mt-28 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {coreAreas.map((area, index) => {
              const isDark = index % 2 === 0;
              return (
                <div
                  key={area.title}
                  className={`p-8 sm:p-10 flex flex-col justify-between items-center text-center rounded-none shadow-xl border transition-transform duration-300 hover:-translate-y-1 group ${
                    isDark
                      ? 'bg-[#0B132B] text-white border-white/10'
                      : 'bg-[#F2EFE9] text-[#1A1A1A] border-[#E2DDD3]'
                  }`}
                >
                  <div className="flex flex-col items-center w-full">
                    
                    {/* Outlined Icon */}
                    <div className={`w-12 h-12 border flex items-center justify-center text-lg mb-4 rounded-none transition-colors ${
                      isDark
                        ? 'border-[#B5A897] text-[#B5A897] group-hover:bg-[#B5A897] group-hover:text-[#0B132B]'
                        : 'border-[#0B132B] text-[#0B132B] group-hover:bg-[#0B132B] group-hover:text-white'
                    }`}>
                      <i className={`fa-solid ${area.icon}`} aria-hidden="true"></i>
                    </div>

                    {/* Small Label */}
                    <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#B5A897] block mb-2">
                      {area.label}
                    </span>

                    {/* Large Numeric Stat / Highlight */}
                    <span className="font-serif text-3xl sm:text-4xl font-normal tracking-widest block mb-4">
                      {area.stat}
                    </span>

                    <div className={`w-8 h-[1px] mb-4 ${isDark ? 'bg-white/20' : 'bg-[#E2DDD3]'}`}></div>

                    {/* Title & Description */}
                    <h3 className="font-serif text-lg uppercase tracking-[0.15em] mb-2 font-normal">
                      {area.title}
                    </h3>

                    <p className={`text-[11px] leading-relaxed mb-6 ${
                      isDark ? 'text-stone-300' : 'text-stone-600'
                    }`}>
                      {area.description}
                    </p>
                  </div>

                  <Link
                    to={area.link}
                    className={`text-[11px] font-medium tracking-[0.2em] uppercase border-b pb-1 transition-all ${
                      isDark
                        ? 'text-white border-white/40 hover:text-[#B5A897] hover:border-[#B5A897]'
                        : 'text-[#0B132B] border-[#0B132B] hover:text-[#B5A897] hover:border-[#B5A897]'
                    }`}
                  >
                    LÆS MERE
                  </Link>
                </div>
              );
            })}
          </div>
        </div>


      </section>

      {/* SECTION 6: FULL-WIDTH INTERIOR LIFESTYLE SHOWCASE WITH PARALLAX EFFECT */}
      <ParallaxBanner
        src="https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/imgi-39-645308290-2053203362125711-8357036613656816799-n.jpg"
        alt="Guldsmedeværksted i Brønshøj"
      />

      {/* SECTION 7: DRAMATIC CONTRAST BLOCK (CENTERED TEXT) */}
      <section className="w-full bg-[#0B132B] text-white min-h-[320px] py-20 px-8 text-center flex flex-col items-center justify-center space-y-4 border-b border-white/10 mx-auto">
        <span className="text-xs uppercase tracking-[0.3em] text-[#B5A897] text-center block">
          KVALITET & FAGLIG STOLTHED
        </span>
        <h3 className="font-serif text-3xl sm:text-5xl uppercase tracking-[0.2em] font-normal text-white max-w-2xl text-center">
          EGET VÆRKSTED DIREKTE I BUTIKKEN
        </h3>
        <p className="text-xs tracking-widest text-stone-300 max-w-md uppercase pt-2 text-center">
          Hurtige reparationer, omforandringer og personlig rådgivning
        </p>
      </section>

      {/* SECTION 8: OPENING HOURS & LOCATION (Centered Cream Card) */}
      <section className="w-full py-24 bg-[#F9F8F6] px-4">
        <div className="bg-[#F2EFE9] border border-[#E2DDD3] max-w-2xl w-full mx-auto p-10 sm:p-16 flex flex-col items-center text-center rounded-none shadow-none">
          
          <div className="w-[1px] h-10 bg-[#B5A897] mb-6"></div>

          <span className="text-[10px] uppercase tracking-[0.25em] text-[#B5A897] font-semibold block mb-1">
            VELKOMMEN I BUTIKKEN
          </span>

          <h2 className="font-serif text-3xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A] mb-8">
            ÅBNINGSTIDER & ADRESSE
          </h2>

          <div className="w-full space-y-1 mb-8">
            {openingHours.map((item) => (
              <div key={item.day} className="flex justify-between items-center py-2.5 border-b border-[#E2DDD3] text-xs uppercase tracking-[0.15em]">
                <span className="text-stone-500 font-medium">{item.day}</span>
                <span className={`font-semibold ${item.hours === 'LUKKET' ? 'text-stone-400' : 'text-[#1A1A1A]'}`}>
                  {item.hours}
                </span>
              </div>
            ))}
          </div>

          <div className="text-xs uppercase tracking-widest text-stone-600 mb-8 space-y-1">
            <p className="font-semibold text-[#1A1A1A]">{companyInfo.name}</p>
            <p>{companyInfo.address}, {companyInfo.zipCity}</p>
            <p className="pt-1 text-[#B5A897] font-medium">TLF: {companyInfo.phone}</p>
          </div>

          <Link
            to="/kontakt"
            className="border border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B] hover:text-white transition-all text-xs tracking-[0.2em] uppercase px-8 py-3.5 rounded-none font-medium"
          >
            FIND VEJ TIL BUTIKKEN
          </Link>

        </div>
      </section>

      {/* SECTION 9: INSTAGRAM FEED */}
      <InstagramSection />
    </>
  );
}
